import api from './api.js';
import { checkToken } from '@/utils/lambdaUtils';
import logger from '@/utils/logger';
import { useAuthStore } from '@/stores/authStore';
import axios from 'axios';

export const baseUrl = `${import.meta.env.VITE_APP_API_BASE_URL}`;

export const annualReportUrls = {
  DI: `/di/annualreport`,
  FOSC: `/fosc/annualreport`,
  CEX: `/cex/annualreport`,
  URO: `/uro/annualreport`
};

/**
 * Makes a GET request to the API with token checking.
 * @param {string} endpoint - The API endpoint
 * @param {Object} params - URL parameters (optional)
 * @returns {Promise<any>} - The response data
 */
export async function apiGet(endpoint, params = {}) {
  await checkToken();
  
  const queryString = Object.entries(params)
    .filter(([, value]) => value !== undefined && value !== null)
    .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
    .join('&');
    
  const url = queryString ? `${baseUrl}${endpoint}?${queryString}` : `${baseUrl}${endpoint}`;
  try {
    const response = await api.get(url);
    return response.data;
  } catch (error) {
    logger.error(`Error fetching data from ${endpoint}:`, error);
    throw error;
  }
}

/**
 * Makes a POST request to the API with token checking.
 * @param {string} endpoint - The API endpoint
 * @param {Object} data - The request body
 * @returns {Promise<any>} - The response data
 */
export async function apiPost(endpoint, data) {
  await checkToken();
  
  try {
    const response = await api.post(`${baseUrl}${endpoint}`, data);
    return response.data;
  } catch (error) {
    logger.error(`Error posting data to ${endpoint}:`, error);
    throw error;
  }
}

/**
 * Sends a POST request with form data to the specified API endpoint.
 *
 * @async
 * @function
 * @param {string} endpoint - The API endpoint to send the request to (relative to baseUrl).
 * @param {FormData} formData - The form data to be sent in the request body.
 * @returns {Promise<any>} The response data from the API.
 * @throws {Error} If the formData is invalid or if the request fails.
 */
export async function apiPostForm(endpoint, formData) {
  if (!formData || !(formData instanceof FormData)) {
    throw new Error('Invalid form data provided');
  }
  const store = useAuthStore();
  await checkToken();

  // try {
    const response = await axios.post(`${baseUrl}${endpoint}`, formData, {
      headers: {
        'Authorization': `Bearer ${store.token}`
      }
    });
    return response.data;
  // } catch (error) {
  //   logger.error(`Error posting file to ${endpoint}:`, error);
  //   return response.response;
  // }
}


/**
 * Makes a PUT request to the API with token checking.
 * @param {string} endpoint - The API endpoint
 * @param {Object} data - The request body
 * @returns {Promise<any>} - The response data
 */
export async function apiPut(endpoint, data) {
  await checkToken();
  
  try {
    const response = await api.put(`${baseUrl}${endpoint}`, data);
    return response.data;
  } catch (error) {
    logger.error(`Error putting data to ${endpoint}:`, error);
    throw error;
  }
}

/**
 * Makes a DELETE request to the API with token checking.
 * @param {string} endpoint - The API endpoint
 * @param {Object} params - URL parameters
 * @returns {Promise<any>} - The response data
 */
export async function apiDelete(endpoint, params = {}) {
  await checkToken();
  
  const queryString = Object.entries(params)
    .filter(([, value]) => value !== undefined && value !== null)
    .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
    .join('&');
    
  const url = queryString ? `${baseUrl}${endpoint}?${queryString}` : `${baseUrl}${endpoint}`;
  
  try {
    const response = await api.delete(url);
    return response.data;
  } catch (error) {
    logger.error(`Error deleting data from ${endpoint}:`, error);
    throw error;
  }
}

/**
 * Uploads a file to the API with token checking.
 * @param {string} endpoint - The API endpoint
 * @param {FormData} formData - The form data with file
 * @param {Object} params - URL parameters (optional)
 * @returns {Promise<any>} - The response data
 */
export async function apiUpload(endpoint, formData, params = {}) {
  const store = useAuthStore();
  await checkToken();
  
  const queryString = Object.entries(params)
    .filter(([, value]) => value !== undefined && value !== null)
    .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
    .join('&');
    
  const url = queryString ? `${baseUrl}${endpoint}?${queryString}` : `${baseUrl}${endpoint}`;
  
  try {
    const response = await axios.post(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        'Authorization': `Bearer ${store.token}`
      }
    });
    return response.data;
  } catch (error) {
    logger.error(`Error uploading to ${endpoint}:`, error);
    throw error;
  }
}

/**
 * Validates that all required parameters are provided and numeric values are greater than zero.
 * @param {Object} params - The parameters to validate
 * @param {boolean} [validateNumeric=true] - If true, numeric values must be greater than zero
 * @throws {Error} If any parameter is undefined, null, empty string, or (for numbers) not greater than zero
 */
export function validateRequiredParams(params, validateNumeric = true) {
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') {
      throw new Error(`Missing required parameter: ${key}`);
    }
    
    if (validateNumeric && typeof value === 'number' && value <= 0) {
      throw new Error(`Parameter ${key} must be greater than zero`);
    }
  }
}